/**
 * LvLogo — the customer's brand mark, the Header's left slot extracted so the
 * Experience Storybook stories the logo through the same production path the
 * Header renders (docs/storybook-regions.md, Header region; no parallel
 * Storybook-only renderer).
 *
 * The contract comes from the brand model (lvConfig): the theme-dependent
 * asset (brand.logoUrlDark when the mode is dark, else brand.logoUrl), its
 * alt text (brand.logoAlt, else the product name), and the model's own
 * fallback — no logo configured renders the deployment's name as text.
 * Sizing is the Header's existing bounds: 34px tall (the approved design's
 * masthead weight), aspect preserved, clamped at 180px wide.
 */
import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import {
  brandLogoUrl, brandLogoUrlDark, brandLogoAlt, productName, type LvClientConfig,
} from './lvConfig'

export default function LvLogo({
  cfg,
  mode,
}: {
  cfg: LvClientConfig | null
  /** Which themed side is rendering — picks the dark-mode asset when 'dark'. */
  mode: 'light' | 'dark'
}) {
  const logoUrl = mode === 'dark' ? brandLogoUrlDark(cfg) : brandLogoUrl(cfg)
  // URLs that failed or proved to be a 1px tracker-style spacer: the classic
  // FormatResource fallback answers a spacer.gif for unbranded branches — a
  // successful load, visibly nothing. Those render the product name instead
  // of an invisible gap in the masthead.
  const [badUrl, setBadUrl] = useState<string | null>(null)
  const showLogo = logoUrl && badUrl !== logoUrl
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1 }}>
      {showLogo ? (
        <Box
          component="img" src={logoUrl} alt={brandLogoAlt(cfg)}
          onError={() => setBadUrl(logoUrl)}
          onLoad={(e) => {
            const img = e.currentTarget
            if (img.naturalWidth <= 2 && img.naturalHeight <= 2) setBadUrl(logoUrl)
          }}
          sx={{ height: 34, width: 'auto', maxWidth: 180, objectFit: 'contain' }}
        />
      ) : (
        <Typography variant="subtitle1" fontWeight={700} noWrap>{productName(cfg)}</Typography>
      )}
    </Box>
  )
}
